import { useState, useEffect } from 'react';
import { Button, Input, SelectBox, Text, Label, Modal, ModalInner } from 'components';
import { InformationCircleOutlineIcon, DuplicateOutlineIcon } from 'assets/svgs';

/* ============================================================
 * Coordinate Conversion (IAU 2006/2000A, IERS 2010)
 *
 * 파이프라인:
 *  1) 시각 UTC→(UT1, TT) 및 T_TT                             [IERS 2010, Ch.5]
 *  2) X,Y,s (CIO) from IERS 2010 tab 5.2a/b/d               [IERS 2010, Tab.5.2a/b/d]
 *  3) CIRS ← GCRS : RC2I = c2ixys(X+dx, Y+dy, s)            [SOFA iauC2ixys]
 *  4) ERA(UT1)                                              [IERS 2010, Eq.(5.15)]
 *  5) TIO s′(TT)                                            [IERS 2010, Eq.(5.24); SOFA iauSp00]
 *  6) Polar motion: RPOM = R3(s′)·R2(xp)·R1(yp)             [IERS 2010, Ch5.4; SOFA iauPom00]
 *  7) GMST06(TT,UT1)                                        [Capitaine+2005; SOFA iauGmst06]
 *  8) dpsi,deps (2000A) from IERS 2010 tab 5.3a/b           [IERS 2010, Tab.5.3a/b]
 *  9) EECT(=complementary terms) from tab 5.2e (GST06a)     [IERS 2010, Tab.5.2e]
 * 10) GAST = GMST + (dpsi cos eps + EECT)                   [IERS 2010, Ch5.5]
 * 11) EO = GAST - ERA                                       [IERS 2010, Ch5.5]
 * 12) TOD = R3(-EO)·RC2I,  MOD = (nutation^-1)·TOD          [SOFA iauPn06a 분해]
 * 13) TIRS = R3(ERA)·RC2I,  ECEF = RPOM·TIRS                [IERS 2010, Ch5.6]
 * ============================================================ */

interface EOPRow {
  mjd: number;
  date: string;
  utc: string;
  xp_arcsec?: number | null;
  yp_arcsec?: number | null;
  dut1_sec: number;
  lod_sec?: number | null;
  dpsi_arcsec?: number | null;
  deps_arcsec?: number | null;
  dx_arcsec?: number | null;
  dy_arcsec?: number | null;
  dat_tai_minus_utc_sec?: number | null;
}

const CoordinateTransformation = () => {
  const [fromFormat, setFromFormat] = useState(1);
  const [toFormat, setToFormat] = useState(2);
  const [inputX, setInputX] = useState<number | string>('');
  const [inputY, setInputY] = useState<number | string>('');
  const [inputZ, setInputZ] = useState<number | string>('');
  const [convertedX, setConvertedX] = useState('');
  const [convertedY, setConvertedY] = useState('');
  const [convertedZ, setConvertedZ] = useState('');
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [eopData, setEopData] = useState<EOPRow[]>([]);

  const coordinateFormats = [
    { value: 1, label: 'J2000' },
    { value: 2, label: 'ECEF' },
    { value: 3, label: 'MOD' },
    { value: 4, label: 'TOD' },
    { value: 5, label: 'PEF' },
    { value: 6, label: 'LLA' },
    { value: 7, label: 'Inertial Frame' }
  ];

  // EOP 데이터 로드
  useEffect(() => {
    const loadEOP = async () => {
      try {
        const response = await fetch('/eop.json');
        const data: EOPRow[] = await response.json();
        setEopData(data);
      } catch (error) {
        console.error('Failed to load EOP data:', error);
      }
    };
    loadEOP();
  }, []);

  /* ---------- Small utils ---------- */
  const d2r = (d: number) => (d * Math.PI) / 180;
  const as2r = (as: number) => (as * Math.PI) / (180 * 3600);
  const wrap2pi = (a: number) => {
    const t = a % (2 * Math.PI);
    return t < 0 ? t + 2 * Math.PI : t;
  };

  const D2PI = 2 * Math.PI;
  const DAS2R = parseFloat('4.848136811095359935899141e-6'); // arcsec → rad
  const AS2R = DAS2R;
  const UAS2RAD = DAS2R / 1e6; // μas → rad
  const TURNAS = 1296000.0;

  /* ---------- Time conversions ---------- */
  const dateToJD = (d: Date) => d.getTime() / 86400000 + 2440587.5;
  const dateToMJD = (d: Date) => d.getTime() / 86400000 + 40587.0;

  /* ---------- Matrix helpers (SOFA와 동일 right-handed 회전) ---------- */
  const R1 = (a: number) => {
    const s = Math.sin(a), c = Math.cos(a);
    return [
      [1, 0, 0],
      [0, c, -s],
      [0, s, c],
    ];
  };
  const R2 = (a: number) => {
    const s = Math.sin(a), c = Math.cos(a);
    return [
      [c, 0, s],
      [0, 1, 0],
      [-s, 0, c],
    ];
  };
  const R3 = (a: number) => {
    const s = Math.sin(a), c = Math.cos(a);
    return [
      [c, -s, 0],
      [s, c, 0],
      [0, 0, 1],
    ];
  };
  const mul = (A: number[][], B: number[][]): number[][] => {
    const C: number[][] = Array.from({ length: 3 }, () => [0, 0, 0]);
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 3; j++)
        C[i][j] = A[i][0] * B[0][j] + A[i][1] * B[1][j] + A[i][2] * B[2][j];
    return C;
  };
  const mv = (A: number[][], v: number[]): number[] => [
    A[0][0] * v[0] + A[0][1] * v[1] + A[0][2] * v[2],
    A[1][0] * v[0] + A[1][1] * v[1] + A[1][2] * v[2],
    A[2][0] * v[0] + A[2][1] * v[1] + A[2][2] * v[2],
  ];
  const tr = (M: number[][]): number[][] => [
    [M[0][0], M[1][0], M[2][0]],
    [M[0][1], M[1][1], M[2][1]],
    [M[0][2], M[1][2], M[2][2]],
  ];
  const eye3 = (): number[][] => [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  /* ---------- EOP linear interpolation ---------- */
  const interpEOP = (eops: EOPRow[], mjd: number) => {
    const safe = <T,>(x: T | null | undefined) => x ?? null;
    if (!eops?.length)
      return { dut1: 0, dat: 0, xp: 0, yp: 0, dx: 0, dy: 0 } as const;

    let lo = 0, hi = eops.length - 1;
    if (mjd <= eops[lo].mjd) {
      const e = eops[lo];
      return {
        dut1: e.dut1_sec,
        dat: safe(e.dat_tai_minus_utc_sec) ?? 0,
        xp: safe(e.xp_arcsec) ?? 0,
        yp: safe(e.yp_arcsec) ?? 0,
        dx: safe(e.dx_arcsec) ?? 0,
        dy: safe(e.dy_arcsec) ?? 0,
      } as const;
    }
    if (mjd >= eops[hi].mjd) {
      const e = eops[hi];
      return {
        dut1: e.dut1_sec,
        dat: safe(e.dat_tai_minus_utc_sec) ?? 0,
        xp: safe(e.xp_arcsec) ?? 0,
        yp: safe(e.yp_arcsec) ?? 0,
        dx: safe(e.dx_arcsec) ?? 0,
        dy: safe(e.dy_arcsec) ?? 0,
      } as const;
    }
    while (hi - lo > 1) {
      const m = (lo + hi) >> 1;
      eops[m].mjd <= mjd ? (lo = m) : (hi = m);
    }
    const a = eops[lo], b = eops[hi], t = (mjd - a.mjd) / (b.mjd - a.mjd);
    const L = (A?: number | null, B?: number | null) =>
      A != null && B != null ? A + t * (B - A) : A ?? B ?? 0;
    return {
      dut1: a.dut1_sec + t * (b.dut1_sec - a.dut1_sec),
      dat: a.dat_tai_minus_utc_sec ?? b.dat_tai_minus_utc_sec ?? 0,
      xp: L(a.xp_arcsec, b.xp_arcsec),
      yp: L(a.yp_arcsec, b.yp_arcsec),
      dx: L(a.dx_arcsec, b.dx_arcsec),
      dy: L(a.dy_arcsec, b.dy_arcsec),
    } as const;
  };

  /* ---------- WGS84 LLA <-> ECEF ---------- */
  const WGS84_A = 6378.137;
  const WGS84_F = 1 / 298.257223563;
  const WGS84_E2 = WGS84_F * (2 - WGS84_F);
  const llaToECEF = (lat: number, lon: number, h: number) => {
    const φ = d2r(lat), λ = d2r(lon);
    const s = Math.sin(φ), c = Math.cos(φ);
    const N = WGS84_A / Math.sqrt(1 - WGS84_E2 * s * s);
    return [
      (N + h) * c * Math.cos(λ),
      (N + h) * c * Math.sin(λ),
      (N * (1 - WGS84_E2) + h) * s,
    ];
  };

  const ecefToLLA = (x: number, y: number, z: number): [number, number, number] => {
    const a = WGS84_A, f = WGS84_F, e2 = WGS84_E2;
    const b = a * (1 - f), ep2 = (a * a - b * b) / (b * b);
    const p = Math.hypot(x, y);
    const th = Math.atan2(z * a, p * b), st = Math.sin(th), ct = Math.cos(th);
    const lon = Math.atan2(y, x);
    const lat = Math.atan2(z + ep2 * b * st ** 3, p - e2 * a * ct ** 3);
    const s = Math.sin(lat), N = a / Math.sqrt(1 - e2 * s * s);
    const h = p / Math.cos(lat) - N;
    return [(lat * 180) / Math.PI, (lon * 180) / Math.PI, h];
  };

  /* ---------- Earth Rotation Angle (IERS 2010 Eq.5.15) ---------- */
  const era_rad = (JD_UT1: number) => {
    const d = JD_UT1 - 2451545.0;
    const f = 0.779057273264 + parseFloat('1.00273781191135448') * d;
    return wrap2pi(D2PI * (f - Math.floor(f)));
  };

  /* ---------- Fundamental arguments (IERS 2010, Ch5.6; SOFA) ---------- */
  const fundLunisolar = (T: number) => {
    const modTurn = (as: number) => ((as % TURNAS) + TURNAS) % TURNAS;
    const L = modTurn(
      485868.249036 +
      T * (1717915923.2178 + T * (31.8792 + T * (0.051635 - 0.0002447 * T)))
    );
    const LP = modTurn(
      1287104.79305 +
      T * (129596581.0481 + T * (-0.5532 + T * (0.000136 - 0.00001149 * T)))
    );
    const F = modTurn(
      335779.526232 +
      T * (1739527262.8478 + T * (-12.7512 + T * (-0.001037 + 0.00000417 * T)))
    );
    const D = modTurn(
      1072260.70369 +
      T * (1602961601.209 + T * (-6.3706 + T * (0.006593 - 0.00003169 * T)))
    );
    const OM = modTurn(
      450160.398036 +
      T * (-6962890.5431 + T * (7.4722 + T * (0.007702 - 0.00005939 * T)))
    );
    return {
      L: L * DAS2R,
      LP: LP * DAS2R,
      F: F * DAS2R,
      D: D * DAS2R,
      OM: OM * DAS2R,
    };
  };
  const anp = (a: number) => ((a % D2PI) + D2PI) % D2PI;
  const fame03 = (T: number) => anp(4.402608842 + 2608.7903141574 * T);
  const fave03 = (T: number) => anp(3.176146697 + 1021.3285546211 * T);
  const fae03 = (T: number) => anp(1.753470314 + 628.3075849991 * T);
  const fama03 = (T: number) => anp(6.203480913 + 334.06124267 * T);
  const faju03 = (T: number) => anp(0.599546497 + 52.9690962641 * T);
  const fasa03 = (T: number) => anp(0.874016757 + 21.329910496 * T);
  const faur03 = (T: number) => anp(5.481293872 + 7.4781598567 * T);
  const fane03 = (T: number) => anp(5.311886287 + 3.8133035638 * T);
  const fapa03 = (T: number) => (0.02438175 + 0.00000538691 * T) * T;

  /* ---------- Mean obliquity (IAU 2006) ---------- */
  const meanObliquity_arcsec = (T: number) => {
    return (
      84381.406 -
      46.836769 * T -
      0.0001831 * T * T +
      0.0020034 * T ** 3 -
      5.76e-7 * T ** 4 -
      4.34e-8 * T ** 5
    );
  };

  /* ---------- GMST06 (Capitaine+2005; SOFA iauGmst06) ---------- */
  const gmst06_rad = (JD_UT1: number, T_TT: number) => {
    const era = era_rad(JD_UT1);
    const p_as =
      0.014506 +
      (4612.156534 +
        (1.3915817 +
          (-0.00000044 + (-0.000029956 - 0.0000000368 * T_TT) * T_TT) * T_TT) *
          T_TT) *
        T_TT;
    return (era + p_as * AS2R) % D2PI;
  };

  /* ---------- IERS 2010 text tables parser (5.2a/b/d, 5.2e, 5.3a/b) ---------- */
  type Term = { j: number; sin: number; cos: number; k: number[] };
  type Series = { poly: number[]; terms: Term[]; scaleRad: number };

  const parseSeries = (txt: string): Series => {
    const lines = txt.split(/\r?\n/);
    const is01uas = lines.some((l) => /0\.1\s*microarcsecond/i.test(l));
    const is1uas = lines.some((l) => /microarcsecond/i.test(l)) && !is01uas;
    const scaleRad = is01uas ? DAS2R / 1e7 : is1uas ? DAS2R / 1e6 : DAS2R / 1e6;
    const series: Series = { poly: [], terms: [], scaleRad };

    const pi = lines.findIndex((l) => /Polynomial part/i.test(l));
    if (pi >= 0) {
      for (let i = pi + 1; i < Math.min(pi + 8, lines.length); i++) {
        const l = lines[i];
        if (!l || /j\s*=\s*\d+/.test(l)) break;
        const tokens = l.match(/[-+]?\d+(?:\.\d+)?(?:\s*\*?\s*t(?:\^\d)?)?/gi) || [];
        for (const tok of tokens) {
          const m = tok.match(/([-+]?\d+(?:\.\d+)?)(?:\s*\*?\s*t(?:\^(\d))?)?/i);
          if (!m) continue;
          const c = parseFloat(m[1]);
          const p = m[2] ? parseInt(m[2], 10) : 0;
          series.poly[p] = (series.poly[p] || 0) + c;
        }
      }
    }

    let j = 0;
    for (const l of lines) {
      const jm = l.match(/^\s*j\s*=\s*(\d+)/i);
      if (jm) {
        j = parseInt(jm[1], 10);
        continue;
      }
      if (/^\s*[-\d.]+\s+[-\d.]+(\s+[-\d]+){14}\s*$/.test(l)) {
        const nums = l.trim().split(/\s+/).map(Number);
        const sin = nums[0];
        const cos = nums[1];
        const k = nums.slice(-14);
        series.terms.push({ j, sin, cos, k });
      }
    }
    return series;
  };

  /* ---------- X,Y,s (tab 5.2a/b/d) ---------- */
  let IERS_SERIES: { ax: Series; ay: Series; asxy: Series } | null = null;
  const loadIersSeries = async () => {
    if (IERS_SERIES) return IERS_SERIES;
    const [tx, ty, ts] = await Promise.all([
      fetch('/iers/tab5.2a.txt', { cache: 'no-store' }).then((r) => r.text()),
      fetch('/iers/tab5.2b.txt', { cache: 'no-store' }).then((r) => r.text()),
      fetch('/iers/tab5.2d.txt', { cache: 'no-store' }).then((r) => r.text()),
    ]);
    IERS_SERIES = { ax: parseSeries(tx), ay: parseSeries(ty), asxy: parseSeries(ts) };
    return IERS_SERIES;
  };

  const evalSeries = (series: Series, T: number, argsRad: number[]) => {
    let a = 0;
    for (let p = 0; p < series.poly.length; p++) a += (series.poly[p] || 0) * Math.pow(T, p);
    for (const { j, sin, cos, k } of series.terms) {
      let arg = 0;
      for (let i = 0; i < 14; i++) arg += (k[i] || 0) * argsRad[i];
      a += Math.pow(T, j) * (sin * Math.sin(arg) + cos * Math.cos(arg));
    }
    return a * series.scaleRad; // → rad
  };

  const xys2006 = async (T: number) => {
    if (!IERS_SERIES) await loadIersSeries();
    const { ax, ay, asxy } = IERS_SERIES!;
    const { L, LP, F, D, OM } = fundLunisolar(T);
    const args = [
      L,
      LP,
      F,
      D,
      OM,
      fame03(T),
      fave03(T),
      fae03(T),
      fama03(T),
      faju03(T),
      fasa03(T),
      faur03(T),
      fane03(T),
      fapa03(T),
    ];
    const X = evalSeries(ax, T, args);
    const Y = evalSeries(ay, T, args);
    const Sxy = evalSeries(asxy, T, args);
    const s = Sxy - 0.5 * X * Y;
    return { X, Y, s };
  };

  /* ---------- dpsi, deps (tab 5.3a/b) ---------- */
  let NUT_SERIES: { dpsi: Series; deps: Series } | null = null;
  const ensureNutSeries = async () => {
    if (NUT_SERIES) return;
    const [tpsi, teps] = await Promise.all([
      fetch('/iers/tab5.3a.txt', { cache: 'no-store' }).then((r) => r.text()),
      fetch('/iers/tab5.3b.txt', { cache: 'no-store' }).then((r) => r.text()),
    ]);
    NUT_SERIES = { dpsi: parseSeries(tpsi), deps: parseSeries(teps) };
  };

  const nutation2000A = (T: number) => {
    const { L, LP, F, D, OM } = fundLunisolar(T);
    const args = [
      L,
      LP,
      F,
      D,
      OM,
      fame03(T),
      fave03(T),
      fae03(T),
      fama03(T),
      faju03(T),
      fasa03(T),
      faur03(T),
      fane03(T),
      fapa03(T),
    ];
    const dpsi = evalSeries(NUT_SERIES!.dpsi, T, args);
    const deps = evalSeries(NUT_SERIES!.deps, T, args);
    return { dpsi, deps, OM };
  };

  /* ---------- EECT (GST06a; tab 5.2e) ---------- */
  let GST_SERIES: Series | null = null;
  const ensureGSTSeries = async () => {
    if (GST_SERIES) return;
    const txt = await fetch('/iers/tab5.2e.txt', { cache: 'no-store' }).then((r) => r.text());
    GST_SERIES = parseSeries(txt);
  };

  const eect_from_5_2e = (T: number) => {
    if (!GST_SERIES) return 0;
    const { L, LP, F, D, OM } = fundLunisolar(T);
    const args = [
      L,
      LP,
      F,
      D,
      OM,
      fame03(T),
      fave03(T),
      fae03(T),
      fama03(T),
      faju03(T),
      fasa03(T),
      faur03(T),
      fane03(T),
      fapa03(T),
    ];
    return evalSeries(GST_SERIES, T, args);
  };

  /* ---------- c2ixys (SOFA) ---------- */
  const c2ixys = (X: number, Y: number, s: number) => {
    const Z = Math.sqrt(Math.max(0, 1 - X * X - Y * Y));
    const a = 1 / (1 + Z);
    const q = [
      [1 - a * X * X, -a * X * Y, X],
      [-a * X * Y, 1 - a * Y * Y, Y],
      [-X, -Y, 1 - a * (X * X + Y * Y)],
    ];
    return mul(R3(s), q);
  };

  /* ---------- s′ (TIO locator; SOFA iauSp00) ---------- */
  const sp00_rad = (T_TT: number) => {
    const arcsec = -0.000047 * T_TT;
    return arcsec * AS2R;
  };

  /* ---------- EECT truncated fallback (if tab 5.2e absent) ---------- */
  const eect00_trunc = (OM: number) => {
    const μas = 2640.96 * Math.sin(OM) + 63.52 * Math.sin(2 * OM);
    return μas * UAS2RAD;
  };

  /* ---------- Build frame set (J2000→{MOD,TOD,PEF,ECEF}) ---------- */
  const buildFrameSetNow_IAU2006 = async (eops: EOPRow[] | null) => {
    // 1) Time scales
    const now = new Date();
    const jdUTC = dateToJD(now);
    const mjdUTC = dateToMJD(now);
    const e = eops?.length
      ? interpEOP(eops, mjdUTC)
      : { dut1: 0, dat: 0, xp: 0, yp: 0, dx: 0, dy: 0 };
    const jdUT1 = jdUTC + (e.dut1 ?? 0) / 86400;
    const jdTT = jdUTC + ((e.dat ?? 0) + 32.184) / 86400;
    const T_TT = (jdTT - 2451545.0) / 36525.0;

    // 2) CIO: X, Y, s (with dX, dY) → RC2I (IERS 5.2a/b/d)
    const { X, Y, s } = await xys2006(T_TT);
    const Xc = X + as2r(e.dx ?? 0);
    const Yc = Y + as2r(e.dy ?? 0);
    const RC2I = c2ixys(Xc, Yc, s); // GCRS → CIRS

    // 3) Earth rotation & polar motion
    const era = era_rad(jdUT1); // ERA(UT1)
    const sPrime = sp00_rad(T_TT); // s'(TT)
    const RPOM = mul(R3(sPrime), mul(R2(as2r(e.xp ?? 0)), R1(as2r(e.yp ?? 0)))); // TIRS → ITRS

    // 4) Equinox path → EO, GAST (IERS 5.3a/b + 5.2e)
    await ensureNutSeries();
    await ensureGSTSeries();
    const { dpsi, deps, OM } = nutation2000A(T_TT);
    const eps0 = meanObliquity_arcsec(T_TT) * AS2R;
    const gmst = gmst06_rad(jdUT1, T_TT);
    const eect = GST_SERIES ? eect_from_5_2e(T_TT) : eect00_trunc(OM);
    const eps = eps0 + deps;
    const ee = dpsi * Math.cos(eps) + eect; // equation of equinoxes
    const gast = wrap2pi(gmst + ee);
    const EO = wrap2pi(gast - era);

    // 5) Frames
    const RNPB = mul(R3(-EO), RC2I); // TOD
    const RN = mul(R1(-eps), mul(R3(-dpsi), R1(eps0))); // nutation matrix
    const RBP = mul(tr(RN), RNPB); // MOD
    const A_TIRS = mul(R3(era), RC2I); // TIRS
    const A_ECEF = mul(RPOM, A_TIRS); // ECEF/ITRS

    const A_J2000 = eye3(); // J2000 = GCRS (간소화)
    return { J2000: A_J2000, MOD: RBP, TOD: RNPB, PEF: A_TIRS, ECEF: A_ECEF, 'Inertial Frame': A_J2000 };
  };

  const handleConvert = async () => {
    const toNum = (s: string | number | undefined | null) => {
      if (s === null || s === undefined || s === '') return null;
      if (typeof s === 'number') {
        return Number.isFinite(s) ? s : null;
      }
      const v = Number(String(s).trim());
      return Number.isFinite(v) ? v : null;
    };

    const vx = toNum(inputX);
    const vy = toNum(inputY);
    const vz = toNum(inputZ);

    if (vx == null || vy == null || vz == null) {
      setAlertMessage('Please enter valid numeric values for all fields');
      setConvertedX('');
      setConvertedY('');
      setConvertedZ('');
      return;
    }

    const fromLabel = coordinateFormats.find(f => f.value === fromFormat)?.label || '';
    const toLabel = coordinateFormats.find(f => f.value === toFormat)?.label || '';

    try {
      // From과 To가 같은 경우 입력값 그대로 반환
      if (fromLabel === toLabel) {
        if (toLabel === 'LLA') {
          setConvertedX(vx.toFixed(8));
          setConvertedY(vy.toFixed(8));
          setConvertedZ(vz.toFixed(3));
        } else {
          setConvertedX(vx.toFixed(6));
          setConvertedY(vy.toFixed(6));
          setConvertedZ(vz.toFixed(6));
        }
        return;
      }

      // 좌표계 변환 매트릭스 구하기 (참고 코드와 동일)
      const mats = await buildFrameSetNow_IAU2006(eopData);
      
      // 입력 좌표 준비
      let vec: number[];
      if (fromLabel === 'LLA') {
        vec = llaToECEF(vx, vy, vz);
      } else {
        vec = [vx, vy, vz];
      }

      // 참고 코드와 동일한 변환 방식
      const src = fromLabel === 'LLA' ? 'ECEF' : fromLabel === 'Inertial Frame' ? 'J2000' : fromLabel;
      const dst = toLabel === 'LLA' ? 'ECEF' : toLabel === 'Inertial Frame' ? 'J2000' : toLabel;
      const Afrom = (mats as any)[src];
      const Ato = (mats as any)[dst];

      const vec_in: number[] = fromLabel === 'LLA' ? vec : [vx!, vy!, vz!];
      let out = mv(Ato, mv(tr(Afrom), vec_in));
      if (toLabel === 'LLA') {
        const [lat, lon, h] = ecefToLLA(out[0], out[1], out[2]);
        out = [lat, lon, h];
      }

      const fmt = (n: number, f: number) => Number.isFinite(n) ? n.toFixed(f) : '';
      if (toLabel === 'LLA') {
        setConvertedX(fmt(out[0], 8));
        setConvertedY(fmt(out[1], 8));
        setConvertedZ(fmt(out[2], 3));
      } else {
        setConvertedX(fmt(out[0], 6));
        setConvertedY(fmt(out[1], 6));
        setConvertedZ(fmt(out[2], 6));
      }
    } catch (error) {
      console.error('Conversion error:', error);
      setAlertMessage('An error occurred during conversion. Please check your input values.');
      setConvertedX('');
      setConvertedY('');
      setConvertedZ('');
    }
  };

  const handleClear = () => {
    setInputX('');
    setInputY('');
    setInputZ('');
    setConvertedX('');
    setConvertedY('');
    setConvertedZ('');
  };

  const handleCopy = async (value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      console.log('Copied:', value);
    } catch (err) {
      console.error('Failed to copy:', err);
      setAlertMessage('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-6 px-4 py-6">
      {alertMessage && (
        <Modal isOpened={true}>
          <ModalInner title="Notice" onClickClose={() => setAlertMessage(null)} className="md:w-[400px]">
            <div className="p-4 pt-0">
              <p className="text-body1 text-center" style={{ whiteSpace: 'pre-wrap' }}>
                {alertMessage}
              </p>
            </div>
            <div className="flex w-full flex-row items-center justify-end bg-bg-transparent_gray_light p-4">
              <Button className="h-9" text="OK" onClick={() => setAlertMessage(null)} size="large" />
            </div>
          </ModalInner>
        </Modal>
      )}
      {/* From / To Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* From Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="mb-4">
            <SelectBox
              className="max-w-none"
              bgColor="layer3"
              label="From"
              list={coordinateFormats}
              value={fromFormat}
              setValue={setFromFormat}
            />
          </div>
          
          <div className="space-y-4">
            <div>
              <Label label={fromFormat === 6 ? 'Lat (deg)' : 'X (km)'} htmlFor="from-lat" />
              <Input
                type="number"
                placeholder={fromFormat === 6 ? 'Enter latitude' : 'Enter X (km)'}
                value={inputX}
                setValue={setInputX}
              />
            </div>
            <div>
              <Label label={fromFormat === 6 ? 'Lon (deg)' : 'Y (km)'} htmlFor="from-lon" />
              <Input
                type="number"
                placeholder={fromFormat === 6 ? 'Enter longitude' : 'Enter Y (km)'}
                value={inputY}
                setValue={setInputY}
              />
            </div>
            <div>
              <Label label={fromFormat === 6 ? 'Alt (km)' : 'Z (km)'} htmlFor="from-alt" />
              <Input
                type="number"
                placeholder={fromFormat === 6 ? 'Enter altitude' : 'Enter Z (km)'}
                value={inputZ}
                setValue={setInputZ}
              />
            </div>
          </div>
        </div>

        {/* To Section */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="mb-4">
            <SelectBox
              className="max-w-none"
              bgColor="layer3"
              label="To"
              list={coordinateFormats}
              value={toFormat}
              setValue={setToFormat}
            />
          </div>
          
          <div className="space-y-4">
            <div>
              <Label label={toFormat === 6 ? 'Lat (deg)' : 'X (km)'} htmlFor="to-lat" />
              <div className="flex items-center gap-2">
              <Input
                type="text"
                value={convertedX}
                setValue={setConvertedX}
                isDisabled
                  className="flex-1"
                />
                <button
                  onClick={() => handleCopy(convertedX)}
                  className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  disabled={!convertedX}
                >
                  <DuplicateOutlineIcon />
                </button>
              </div>
            </div>
            <div>
              <Label label={toFormat === 6 ? 'Lon (deg)' : 'Y (km)'} htmlFor="to-lon" />
              <div className="flex items-center gap-2">
              <Input
                type="text"
                value={convertedY}
                setValue={setConvertedY}
                isDisabled
                  className="flex-1"
                />
                <button
                  onClick={() => handleCopy(convertedY)}
                  className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  disabled={!convertedY}
                >
                  <DuplicateOutlineIcon />
                </button>
              </div>
            </div>
            <div>
              <Label label={toFormat === 6 ? 'Alt (km)' : 'Z (km)'} htmlFor="to-alt" />
              <div className="flex items-center gap-2">
              <Input
                type="text"
                value={convertedZ}
                setValue={setConvertedZ}
                isDisabled
                  className="flex-1"
                />
                <button
                  onClick={() => handleCopy(convertedZ)}
                  className="p-1 hover:bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  disabled={!convertedZ}
                >
                  <DuplicateOutlineIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Convert Button */}
      <div className="flex gap-4">
        <Button
          type="blue"
          text="Convert"
          size="large"
          onClick={handleConvert}
          className="flex-1 items-center justify-center"
        />
        <Button
          type="secondary"
          text="Clear"
          size="large"
          onClick={handleClear}
          className="flex-1 items-center justify-center"
        />
      </div>

      {/* Coordinate System Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <InformationCircleOutlineIcon />
          <Text size="heading3" weight="medium" color="black">
            Coordinate System Information
          </Text>
        </div>
        
        <div className="space-y-3">
          <div className="bg-white p-4 rounded border">
            <Text size="body2" weight="medium" color="black">
              J2000 (ECI, Earth-Centered Inertial):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Earth-Centered Inertial frame referenced to the mean equator and equinox of J2000 epoch. Fixed in space, independent of Earth&apos;s rotation.
            </Text>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <Text size="body2" weight="medium" color="black">
              MOD (Mean Equinox of Date):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Mean equator and equinox of the given date. Accounts for precession, but excludes nutation effects.
            </Text>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <Text size="body2" weight="medium" color="black">
              TOD (True of Date):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              True equator and equinox of date. Includes both precession and nutation, representing the actual celestial equinox.
            </Text>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <Text size="body2" weight="medium" color="black">
              PEF (Pseudo Earth Fixed):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Pseudo Earth Fixed frame, aligned with Earth&apos;s rotation (UT1) but before applying polar motion corrections.
            </Text>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <Text size="body2" weight="medium" color="black">
              ECEF (Earth-Centered, Earth-Fixed):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Earth-Centered, Earth-Fixed frame. Accounts for Earth&apos;s rotation and polar motion. Fixed relative to Earth&apos;s surface.
            </Text>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <Text size="body2" weight="medium" color="black">
              Inertial Frame:
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Inertial frame where Newton&apos;s laws hold without fictitious forces. Commonly used in orbital mechanics.
            </Text>
          </div>
          
          <div className="bg-white p-4 rounded border">
            <Text size="body2" weight="medium" color="black">
              LLA (Longitude / Latitude / Altitude):
            </Text>
            <Text size="body2" weight="regular" color="secondary">
              Geodetic coordinate system defined by longitude, latitude, and altitude above reference ellipsoid.
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoordinateTransformation;
